// CardsLayer.jsx (already has responsive grid, no changes needed)
import { useNavigate } from "react-router-dom";
import { useInfrastructureCache } from "../../../hooks/useInfrastructureCache";

const CardsLayer = ({ filter, dateRange }) => {
  const navigate = useNavigate();
  const { data: infrastructure, isLoading } = useInfrastructureCache();

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <div
            key={i}
            className="bg-card-bg rounded-xl border border-border-color p-4 sm:p-6 animate-pulse"
          >
            <div className="h-6 bg-bg-main rounded mb-4"></div>
            <div className="h-4 bg-bg-main rounded mb-2"></div>
            <div className="h-4 bg-bg-main rounded"></div>
          </div>
        ))}
      </div>
    );
  }

  if (!infrastructure) {
    return (
      <div className="bg-card-bg rounded-xl border border-border-color p-8 sm:p-12 text-center">
        <p className="text-primary-dark/60">No infrastructure data available</p>
      </div>
    );
  }

  const { vms, tags, vmTags } = infrastructure;

  const getVmsByTagId = (tagId) => {
    const vmIds = vmTags
      .filter((relation) => relation.tagId === tagId)
      .map((relation) => relation.vmId);
    return vms.filter((vm) => vmIds.includes(vm.id));
  };

  const getGroupedVmsByCategory = (category) => {
    const categoryTags = tags.filter((tag) => tag.key === category);

    const groups = categoryTags
      .map((tag) => ({
        id: tag.id,
        name: tag.value,
        category: tag.key,
        vms: getVmsByTagId(tag.id),
        count: getVmsByTagId(tag.id).length,
      }))
      .filter((group) => group.vms.length > 0);

    return groups;
  };

  const applyDateFilter = (vmsList) => {
    if (!dateRange?.from && !dateRange?.to) return vmsList;
    return vmsList;
  };

  const VmCard = ({ vm, disableHover = false, disableClick = false }) => {
    const handleClick = () => {
      if (!disableClick) {
        navigate(`/productDetail/vm/${vm.name}/Performance`, { 
          state: { 
            vmIds: [vm.id],
            vmNames: [vm.name]
          } 
        });
      }
    };

    const isDeallocated = vm.status === "PowerState/deallocated";
    const displayStatus = isDeallocated ? "Stopped" : "Running";

    return (
      <div
        onClick={handleClick}
        className={`
          bg-card-bg rounded-xl border border-border-color p-4 sm:p-5 transition-all
          ${!disableHover && !disableClick ? "hover:shadow-lg hover:border-primary-cyan/30 cursor-pointer" : ""}
          ${disableClick ? "cursor-default" : ""}
        `}
      >
        <div className="flex justify-between items-start mb-3 flex-wrap gap-2">
          <h3 className="text-base sm:text-lg font-semibold text-primary-dark break-words flex-1">{vm.name}</h3>
          <span className={`text-xs px-2 py-1 rounded-full font-medium whitespace-nowrap ${isDeallocated ? "bg-red-500/20 text-red-500" : "bg-green-500/20 text-green-500"}`}>
            {displayStatus}
          </span>
        </div>

        <div className="space-y-2 text-sm">
          <div className="flex justify-between flex-wrap gap-1">
            <span className="text-primary-dark/60">Type:</span>
            <span className="text-primary-dark font-medium break-words text-right">
              {vm.vmType || "N/A"}
            </span>
          </div>
          <div className="flex justify-between flex-wrap gap-1">
            <span className="text-primary-dark/60">Region:</span>
            <span className="text-primary-dark font-medium break-words text-right">
              {vm.region || "N/A"}
            </span>
          </div>
          <div className="flex justify-between flex-wrap gap-1">
            <span className="text-primary-dark/60">Billing:</span>
            <span className="text-primary-dark font-medium break-words text-right">
              {vm.billingType || "N/A"}
            </span>
          </div>
          {vm.publicIpAddress && (
            <div className="flex justify-between flex-wrap gap-1">
              <span className="text-primary-dark/60">Public IP:</span>
              <span className="text-primary-dark font-medium font-mono text-xs break-all text-right">
                {vm.publicIpAddress}
              </span>
            </div>
          )}
        </div>
      </div>
    );
  };

  const AllVmsView = () => {
    const allVms = applyDateFilter(vms);

    return (
      <div>
        <div className="mb-4 sm:mb-6">
          <h2 className="text-xl sm:text-2xl font-bold text-primary-dark">
            All Virtual Machines
          </h2>
          <p className="text-primary-dark/60 mt-1 text-sm sm:text-base">{allVms.length} total VMs</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {allVms.map((vm) => (
            <VmCard
              key={vm.id}
              vm={vm}
              disableHover={false}
              disableClick={false}
            />
          ))}
        </div>
      </div>
    );
  };

  const GroupedView = ({ category }) => {
    const groups = getGroupedVmsByCategory(category);

    if (groups.length === 0) {
      return (
        <div className="bg-card-bg rounded-xl border border-border-color p-8 sm:p-12 text-center">
          <p className="text-primary-dark/60">
            No VMs found with {category} tags
          </p>
        </div>
      );
    }

    const handleGroupClick = (clickedVmIds, clickedVmNames) => {
      const vmNamesParam = clickedVmNames.join(',');
      navigate(`/productDetail/vm/${vmNamesParam}/Performance`, { 
        state: { 
          vmIds: clickedVmIds,
          vmNames: clickedVmNames
        } 
      });
    };

    return (
      <div className="space-y-4 sm:space-y-6">
        {groups.map((group) => (
          <div
            key={group.id}
            onClick={() => handleGroupClick(
              group.vms.map((vm) => vm.id),
              group.vms.map((vm) => vm.name)
            )}
            className="bg-card-bg rounded-xl border border-border-color overflow-hidden shadow-sm cursor-pointer transition-all hover:border-primary-cyan/50 hover:shadow-lg"
          >
            <div className="bg-primary-dark px-4 sm:px-6 py-3 sm:py-4">
              <div className="flex justify-between items-center flex-wrap gap-2">
                <div>
                  <h2 className="text-lg sm:text-xl font-semibold text-white break-words">
                    {group.name}
                  </h2>
                  <p className="text-white/70 text-xs sm:text-sm mt-1">{group.category}</p>
                </div>
                <div className="bg-white/20 rounded-full px-2 sm:px-3 py-1">
                  <span className="text-white font-medium text-sm sm:text-base">
                    {group.count} VM{group.count !== 1 ? "s" : ""}
                  </span>
                </div>
              </div>
            </div>

            <div className="p-4 sm:p-6">
              {group.vms.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                  {applyDateFilter(group.vms).map((vm) => (
                    <VmCard
                      key={vm.id}
                      vm={vm}
                      disableHover={true}
                      disableClick={true}
                    />
                  ))}
                </div>
              ) : (
                <p className="text-primary-dark/60 text-center py-8">
                  No VMs in this group
                </p>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  if (!filter || filter.type === "all") {
    return <AllVmsView />;
  }

  return <GroupedView category={filter.type} />;
};

export default CardsLayer;